import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Mail, Upload, FileText, Loader2, Sparkles, CheckCircle,
  Edit3, ArrowLeft, X, Send, Clipboard, Paperclip, Image,
  AlertTriangle, FileCheck, Car, ChevronDown
} from 'lucide-react'
import { toast } from 'sonner'
import PreDeclarationFlotte from '../../../components/sinistre/PreDeclarationFlotte'

const API = process.env.REACT_APP_BACKEND_URL
const MAX_FILES = 5
const MAX_FILE_SIZE_MB = 10
const GEST_SESSION_KEY = 'c2_gest_predecl_session'

function C1Avatar({ size = 28 }) {
  return (
    <div
      className="shrink-0 flex items-center justify-center rounded-full font-bold text-white"
      style={{
        width: size, height: size, fontSize: size * 0.38,
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        boxShadow: '0 1px 4px rgba(30,64,175,0.25)',
        letterSpacing: '-0.5px',
      }}
      data-testid="c1-avatar"
    >
      C1
    </div>
  )
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' o'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Ko'
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'
}

function validateAndAddFiles(incoming, current, setFn) {
  const available = MAX_FILES - current.length
  if (available <= 0) {
    toast.error(`Maximum ${MAX_FILES} fichiers. Envoyez d'abord ceux-ci.`)
    return
  }
  const toAdd = incoming.slice(0, available)
  if (incoming.length > available) {
    toast.warning(`Seuls ${available} fichier(s) supplementaire(s) accepte(s).`)
  }
  const valid = []
  for (const f of toAdd) {
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`"${f.name}" depasse ${MAX_FILE_SIZE_MB} Mo.`)
    } else {
      valid.push(f)
    }
  }
  if (valid.length) setFn(prev => [...prev, ...valid])
}

export default function GestionnairePreDecl({ tenants }) {
  const navigate = useNavigate()
  const [tenantId, setTenantId] = useState('')
  const [initialized, setInitialized] = useState(false)
  const [restoring, setRestoring] = useState(false)

  // Police/contract state
  const [polices, setPolices] = useState([])
  const [loadingPolices, setLoadingPolices] = useState(false)
  const [selectedPoliceId, setSelectedPoliceId] = useState('')

  // Chat state
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [files, setFiles] = useState([])
  const [sending, setSending] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [userMsgCount, setUserMsgCount] = useState(0)

  // Initial input
  const [emailText, setEmailText] = useState('')
  const [initFiles, setInitFiles] = useState([])

  // Pre-declaration state
  const [preDeclaration, setPreDeclaration] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editedData, setEditedData] = useState({})
  const [converting, setConverting] = useState(false)
  const [declarationDraft, setDeclarationDraft] = useState(null)
  const [draftLoading, setDraftLoading] = useState(false)

  const chatEndRef = useRef(null)
  const initFileRef = useRef(null)
  const chatFileRef = useRef(null)

  // ── Restore session on mount ──
  useEffect(() => {
    const saved = localStorage.getItem(GEST_SESSION_KEY)
    if (!saved) return
    try {
      const parsed = JSON.parse(saved)
      if (parsed.session_id && parsed.tenant_id) {
        setRestoring(true)
        setTenantId(parsed.tenant_id)
        fetch(`${API}/api/pre-declarations/${parsed.session_id}`)
          .then(r => r.json())
          .then(data => {
            const pd = data.pre_declaration
            if (pd && pd.statut === 'brouillon') {
              const history = pd.messages_chat || []
              if (history.length > 0) {
                const restored = history.map(m => ({
                  role: m.role, content: m.content,
                  files: m.files ? m.files.map(f => ({ name: f, type: '' })) : undefined,
                }))
                setMessages([
                  { role: 'assistant', content: 'C1 — Session restauree. Continuez la conversation ou finalisez la pre-declaration.' },
                  ...restored,
                ])
                setSessionId(parsed.session_id)
                setUserMsgCount(history.filter(m => m.role === 'user').length)
                setInitialized(true)
                if (pd.donnees_structurees && Object.keys(pd.donnees_structurees).length > 0) {
                  setPreDeclaration(pd)
                  setEditedData(pd.donnees_structurees)
                }
              }
            }
          })
          .catch(() => {})
          .finally(() => setRestoring(false))
      }
    } catch { setRestoring(false) }
  }, [])

  // Fetch polices when tenant changes
  useEffect(() => {
    setPolices([])
    setSelectedPoliceId('')
    if (!tenantId) return
    setLoadingPolices(true)
    fetch(`${API}/api/polices?tenant_id=${tenantId}`)
      .then(r => r.json())
      .then(d => setPolices((d.polices || []).filter(p => p.statut === 'en_cours')))
      .catch(() => {})
      .finally(() => setLoadingPolices(false))
  }, [tenantId])

  // Derived: is the selected police a fleet?
  const selectedPolice = polices.find(p => p.id === selectedPoliceId)
  const isFlotte = selectedPolice?.type_police === 'flotte'

  // Save session to localStorage
  useEffect(() => {
    if (sessionId && tenantId) {
      localStorage.setItem(GEST_SESSION_KEY, JSON.stringify({ session_id: sessionId, tenant_id: tenantId }))
    }
  }, [sessionId, tenantId])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Send message (shared by init + chat) ──
  async function sendMessage(text, attachedFiles) {
    setSending(true)
    const userMsg = {
      role: 'user', content: text,
      files: attachedFiles.length > 0 ? attachedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })) : undefined,
    }
    setMessages(prev => [...prev, userMsg])

    try {
      const formData = new FormData()
      formData.append('message', text)
      formData.append('tenant_id', tenantId)
      formData.append('source', 'email_gestionnaire')
      if (sessionId) formData.append('session_id', sessionId)
      for (const f of attachedFiles) {
        formData.append('files', f)
      }
      const res = await fetch(`${API}/api/pre-declarations/chat`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Erreur ${res.status}`)
      }
      const data = await res.json()
      if (data.session_id) setSessionId(data.session_id)
      if (data.assistant_message) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.assistant_message }])
      }
      if (data.pre_declaration) {
        setPreDeclaration(data.pre_declaration)
        setEditedData(data.pre_declaration.donnees_structurees || {})
      }
      setUserMsgCount(prev => prev + 1)
    } catch (err) {
      toast.error(err.message || 'Erreur de communication')
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur de communication. Reessayez.' }])
    } finally {
      setSending(false)
    }
  }

  // ── Initial analyze ──
  async function handleInitialAnalyze() {
    if (!tenantId) { toast.error('Selectionnez un client'); return }
    if (!emailText.trim() && initFiles.length === 0) { toast.error('Collez un email ou ajoutez des fichiers'); return }
    setInitialized(true)
    setMessages([{
      role: 'assistant',
      content: 'Je suis C1, l\'assistant IA C2 Assurances. J\'analyse votre contenu pour generer une pre-declaration. Vous pourrez affiner le resultat par la conversation.'
    }])
    const text = emailText.trim() || '(Documents joints a analyser)'
    await sendMessage(text, initFiles)
    setInitFiles([])
  }

  // ── Chat send ──
  async function handleChatSend(overrideText) {
    const text = overrideText || input.trim()
    if (!text && files.length === 0) return
    if (!overrideText) setInput('')
    const chatFiles = [...files]
    setFiles([])
    await sendMessage(text, chatFiles)
  }

  function handleRequestFinalize() {
    handleChatSend("Genere maintenant la pre-declaration structuree avec toutes les informations dont tu disposes. Si des champs manquent, mets 'A completer'.")
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleChatSend()
    }
  }

  // ── Convert + Draft ──
  async function handleConvert() {
    if (!preDeclaration) return
    setConverting(true)
    try {
      const res = await fetch(`${API}/api/pre-declarations/${preDeclaration.id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ donnees_structurees: editMode ? editedData : preDeclaration.donnees_structurees }),
      })
      const data = await res.json()
      if (data.success && data.sinistre_id) {
        toast.success('Sinistre cree !')
        localStorage.removeItem(GEST_SESSION_KEY)
        navigate(`/c2/sinistre/${data.sinistre_id}`)
      } else {
        toast.error(data.error || 'Erreur conversion')
      }
    } catch { toast.error('Erreur conversion') }
    finally { setConverting(false) }
  }

  async function handleDraftDeclaration() {
    if (!preDeclaration) return
    setDraftLoading(true)
    try {
      const res = await fetch(`${API}/api/pre-declarations/${preDeclaration.id}/draft-declaration`, { method: 'POST' })
      const data = await res.json()
      if (data.declaration) {
        setDeclarationDraft(data.declaration)
        toast.success('Brouillon declaration assureur genere')
      } else { toast.error('Erreur generation') }
    } catch { toast.error('Erreur generation') }
    finally { setDraftLoading(false) }
  }

  function handleNewSession() {
    localStorage.removeItem(GEST_SESSION_KEY)
    setInitialized(false)
    setMessages([])
    setSessionId(null)
    setPreDeclaration(null)
    setDeclarationDraft(null)
    setEditedData({})
    setEmailText('')
    setInitFiles([])
    setFiles([])
    setUserMsgCount(0)
    setTenantId('')
    setSelectedPoliceId('')
    setPolices([])
  }

  const tenantName = (tenants || []).find(t => t.id === tenantId)?.nom || ''

  function policeLabel(p) {
    const type = p.type_police === 'flotte' ? 'Flotte Auto' : p.type_police || ''
    const label = p.libelle && p.libelle !== p.type_police ? p.libelle : ''
    const desc = label || type || 'Police'
    return `${p.numero_police} — ${desc}`
  }

  // ── Loading ──
  if (restoring) {
    return (
      <div className="p-6 max-w-4xl mx-auto flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-indigo-500 mr-3" />
        <span className="text-sm text-slate-500">Restauration de la session...</span>
      </div>
    )
  }

  // ══════════════════════════════════════════════
  // PHASE 1: Initial setup form
  // ══════════════════════════════════════════════
  if (!initialized) {
    // If a fleet police is selected, render the fleet-specific form
    if (isFlotte) {
      return (
        <div className="p-6 max-w-4xl mx-auto" data-testid="gestionnaire-predecl">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate('/c2/pre-declarations')} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-btn">
              <ArrowLeft size={18} className="text-slate-500" />
            </button>
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Car size={16} className="text-amber-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Pre-declaration — Flotte auto</h1>
              <p className="text-xs text-slate-400">
                {tenantName} — {selectedPolice?.numero_police || 'Contrat flotte'}
              </p>
            </div>
          </div>

          {/* Client + Contract selectors on top */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Client</label>
                <select
                  value={tenantId}
                  onChange={e => setTenantId(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="select-tenant"
                >
                  <option value="">Selectionnez un client...</option>
                  {(tenants || []).map(t => (
                    <option key={t.id} value={t.id}>{t.nom}</option>
                  ))}
                </select>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Contrat</label>
                <select
                  value={selectedPoliceId}
                  onChange={e => setSelectedPoliceId(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="select-police"
                >
                  <option value="">Selectionnez un contrat...</option>
                  {polices.map(p => (
                    <option key={p.id} value={p.id}>
                      {policeLabel(p)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <PreDeclarationFlotte
            tenantId={tenantId}
            policeId={selectedPoliceId}
            onCreated={(id) => navigate(`/c2/sinistre/${id}`)}
            onCancel={() => setSelectedPoliceId('')}
            embedded
          />
        </div>
      )
    }

    // Standard C1 chat form (non-fleet)
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="gestionnaire-predecl">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/c2/pre-declarations')} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-btn">
            <ArrowLeft size={18} className="text-slate-500" />
          </button>
          <C1Avatar size={32} />
          <div>
            <h1 className="text-lg font-bold text-slate-900">Creer une pre-declaration</h1>
            <p className="text-xs text-slate-400">C1 analysera le contenu et ouvrira une conversation</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Client selector */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Client</label>
            <select
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="select-tenant"
            >
              <option value="">Selectionnez un client...</option>
              {(tenants || []).map(t => (
                <option key={t.id} value={t.id}>{t.nom}</option>
              ))}
            </select>
          </div>

          {/* Contract/Police selector — shown when tenant is selected */}
          {tenantId && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Contrat {loadingPolices && <Loader2 size={11} className="inline animate-spin ml-1" />}
              </label>
              {polices.length > 0 ? (
                <select
                  value={selectedPoliceId}
                  onChange={e => setSelectedPoliceId(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="select-police"
                >
                  <option value="">-- Optionnel : preciser le contrat --</option>
                  {polices.map(p => (
                    <option key={p.id} value={p.id}>
                      {policeLabel(p)}
                    </option>
                  ))}
                </select>
              ) : !loadingPolices ? (
                <p className="text-xs text-slate-400 py-1">Aucun contrat actif pour ce client</p>
              ) : null}
              {/* Fleet detection indicator */}
              {polices.some(p => p.type_police === 'flotte') && !selectedPoliceId && (
                <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
                  <Car size={13} className="text-amber-500" />
                  <span className="text-xs text-amber-700">
                    Ce client a un contrat <strong>Flotte Auto</strong>. Selectionnez-le pour acceder au formulaire dedie.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Email paste */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              <Mail size={12} className="inline mr-1" />
              Contenu de l'email (optionnel)
            </label>
            <textarea
              value={emailText}
              onChange={e => setEmailText(e.target.value)}
              placeholder="Collez ici le contenu de l'email du client..."
              rows={6}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
              data-testid="email-textarea"
            />
          </div>

          {/* File upload — always visible */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <label className="text-xs font-medium text-slate-600 mb-2 block">
              <Paperclip size={12} className="inline mr-1" />
              Documents joints (max {MAX_FILES} fichiers)
            </label>
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-300 transition-colors"
              onClick={() => initFileRef.current?.click()}
              data-testid="init-dropzone"
            >
              <Upload className="w-5 h-5 text-slate-300 mx-auto mb-1.5" />
              <p className="text-xs text-slate-500">Cliquez pour ajouter des fichiers (PDF, images, docs)</p>
              <p className="text-xs text-slate-400 mt-0.5">{initFiles.length}/{MAX_FILES} fichier(s)</p>
            </div>
            <input
              ref={initFileRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,image/*"
              onChange={e => {
                validateAndAddFiles(Array.from(e.target.files || []), initFiles, setInitFiles)
                if (initFileRef.current) initFileRef.current.value = ''
              }}
              className="hidden"
            />

            {initFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {initFiles.map((f, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded-md" data-testid={`init-file-${i}`}>
                    {f.type?.startsWith('image/') ? <Image size={11} className="text-blue-500" /> : <FileText size={11} className="text-red-500" />}
                    <span className="max-w-[150px] truncate">{f.name}</span>
                    <span className="text-slate-400">({formatFileSize(f.size)})</span>
                    <button onClick={() => setInitFiles(prev => prev.filter((_, j) => j !== i))} className="ml-0.5 hover:text-red-500"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleInitialAnalyze}
            disabled={sending || (!emailText.trim() && initFiles.length === 0)}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            data-testid="analyze-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {sending ? 'Analyse en cours...' : 'Analyser avec C1'}
          </button>
        </div>
      </div>
    )
  }

  // ══════════════════════════════════════════════
  // PHASE 2: Chat conversation + results
  // ══════════════════════════════════════════════
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="gestionnaire-predecl-chat">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={handleNewSession} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-to-form-btn">
          <ArrowLeft size={18} className="text-slate-500" />
        </button>
        <C1Avatar size={28} />
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">Pre-declaration — {tenantName}</h1>
          <p className="text-xs text-slate-400">C1 — Affinez le resultat par la conversation</p>
        </div>
        {preDeclaration && (
          <span className="flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md">
            <CheckCircle size={12} /> Prete
          </span>
        )}
        <button onClick={handleNewSession} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 border border-slate-200 rounded-md" data-testid="new-session-btn">
          Nouvelle
        </button>
      </div>

      {/* Chat area */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ minHeight: '420px' }}>
        <div className="p-4 space-y-3 overflow-y-auto" style={{ maxHeight: '400px' }} data-testid="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
              {msg.role === 'assistant' && <C1Avatar size={22} />}
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-br-sm'
                  : 'bg-slate-100 text-slate-700 rounded-bl-sm'
              }`} data-testid={`chat-msg-${msg.role}`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {msg.files && msg.files.length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {msg.files.map((f, j) => (
                      <div key={j} className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded">
                        {f.type?.startsWith('image/') ? <Image size={10} /> : <FileText size={10} />}
                        <span>{f.name}</span>
                        {f.size && <span className="opacity-60">({formatFileSize(f.size)})</span>}
                        <CheckCircle size={10} className="text-green-300 ml-auto" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start gap-2">
              <C1Avatar size={22} />
              <div className="bg-slate-100 text-slate-500 px-4 py-2.5 rounded-xl rounded-bl-sm text-sm flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> C1 analyse...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Finalize button */}
        {userMsgCount >= 1 && !preDeclaration && !sending && (
          <div className="mx-4 mb-3" data-testid="finalize-section">
            <button
              onClick={handleRequestFinalize}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="finalize-btn"
            >
              <FileCheck size={16} />
              Generer la pre-declaration structuree
            </button>
            <p className="text-xs text-slate-400 text-center mt-1">Ou continuez la conversation pour ajouter des details</p>
          </div>
        )}

        {/* Pre-declaration preview */}
        {preDeclaration && (
          <div className="border-t border-slate-200 p-4 bg-amber-50" data-testid="pre-declaration-preview">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={15} className="text-amber-500" />
                <h3 className="text-sm font-semibold text-slate-700">Pre-declaration generee par C1</h3>
              </div>
              <button onClick={() => setEditMode(!editMode)} className="flex items-center gap-1 text-xs text-indigo-600 font-medium" data-testid="edit-btn">
                <Edit3 size={12} /> {editMode ? 'Apercu' : 'Modifier'}
              </button>
            </div>

            {editMode ? (
              <div className="space-y-2">
                {Object.entries(editedData).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 w-36 shrink-0 text-right">{key.replace(/_/g, ' ')}:</label>
                    <input value={value || ''} onChange={e => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 bg-white" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(preDeclaration.donnees_structurees || {}).map(([key, value]) => (
                  value && (
                    <div key={key}>
                      <span className="text-slate-400">{key.replace(/_/g, ' ')}:</span>{' '}
                      <span className="text-slate-700 font-medium">{String(value)}</span>
                    </div>
                  )
                ))}
              </div>
            )}

            {preDeclaration.analyse_ia?.police_identifiee && (
              <div className="mt-2 px-2 py-1.5 bg-emerald-100 rounded-md text-xs text-emerald-700">
                Police identifiee: {preDeclaration.analyse_ia.police_identifiee}
              </div>
            )}

            <div className="flex items-center gap-3 mt-3">
              <button onClick={handleConvert} disabled={converting}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                data-testid="convert-btn"
              >
                {converting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                Convertir en sinistre
              </button>
              <button onClick={handleDraftDeclaration} disabled={draftLoading}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                data-testid="draft-declaration-btn"
              >
                {draftLoading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Rediger declaration assureur
              </button>
            </div>
          </div>
        )}

        {/* Chat input bar */}
        <div className="border-t border-slate-200 p-3">
          {files.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-slate-500">{files.length}/{MAX_FILES} fichier(s) pret(s)</span>
                {files.length >= MAX_FILES && (
                  <span className="flex items-center gap-1 text-xs text-amber-600"><AlertTriangle size={11} /> Limite atteinte</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {files.map((f, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded-md" data-testid={`chat-file-${i}`}>
                    {f.type?.startsWith('image/') ? <Image size={11} className="text-blue-500" /> : <FileText size={11} className="text-red-500" />}
                    <span className="max-w-[120px] truncate">{f.name}</span>
                    <span className="text-slate-400">({formatFileSize(f.size)})</span>
                    <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="ml-0.5 hover:text-red-500"><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button
              onClick={() => {
                if (files.length >= MAX_FILES) { toast.error(`Maximum ${MAX_FILES} fichiers par envoi.`); return }
                chatFileRef.current?.click()
              }}
              className={`p-2 rounded-md shrink-0 ${files.length >= MAX_FILES ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-slate-100 text-slate-400'}`}
              title="Joindre un fichier"
              data-testid="attach-file-btn"
            >
              <Paperclip size={16} />
            </button>
            <input
              ref={chatFileRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,image/*"
              onChange={e => {
                validateAndAddFiles(Array.from(e.target.files || []), files, setFiles)
                if (chatFileRef.current) chatFileRef.current.value = ''
              }}
              className="hidden"
            />
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Completez ou corrigez la pre-declaration..."
              rows={1}
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
              disabled={sending}
              data-testid="chat-input"
            />
            <button
              onClick={() => handleChatSend()}
              disabled={sending || (!input.trim() && files.length === 0)}
              className="p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-40 shrink-0"
              data-testid="send-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Declaration draft */}
      {declarationDraft && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5" data-testid="declaration-draft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Brouillon declaration assureur</h3>
            <button onClick={() => { navigator.clipboard.writeText(declarationDraft); toast.success('Copie !') }}
              className="flex items-center gap-1 text-xs text-indigo-600 font-medium" data-testid="copy-draft-btn">
              <Clipboard size={12} /> Copier
            </button>
          </div>
          <pre className="text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-md leading-relaxed">{declarationDraft}</pre>
        </div>
      )}
    </div>
  )
}
